import { parse } from 'graphql';
const findMatchingSelection = (selections, key, wildcardSelection) => {
    if (!selections)
        return wildcardSelection || null;
    return selections.find(sel => sel.key === key) || wildcardSelection || null;
};
const getWildcardSelection = (selections) => {
    return selections?.find(s => s.key === '*');
};
const getExplicitlySelectedKeys = (selections) => {
    if (!selections)
        return new Set();
    return new Set(selections
        .filter(s => s.key !== '*')
        .map(s => s.key)
        .filter((key) => Boolean(key)));
};
const selectFieldsFromCollection = (collection, selections, fieldSelector) => {
    if (!selections || selections.length === 0) {
        return [];
    }
    const wildcardSelection = getWildcardSelection(selections);
    const explicitKeys = getExplicitlySelectedKeys(selections);
    const selectedFields = [];
    for (const selection of selections) {
        if (selection.key === '*')
            continue;
        const field = collection.find(f => f.key === selection.key);
        if (field) {
            const selected = fieldSelector(selection, field);
            if (selected) {
                selectedFields.push(selected);
            }
        }
    }
    if (wildcardSelection) {
        const wildcardFields = collection
            .filter(field => !explicitKeys.has(field.key))
            .map(field => fieldSelector(wildcardSelection, field))
            .filter(Boolean);
        selectedFields.push(...wildcardFields);
    }
    return selectedFields;
};
const selectScalar = (selection, config) => {
    return { ...config };
};
const selectField = (selection, config) => {
    const wildcardArgSelection = getWildcardSelection(selection.args);
    const selectedArgs = selectFieldsFromCollection(config.args || [], selection.args, (argSelection, arg) => selectInputValue(argSelection, arg));
    const selectedOfType = selection.selections
        ? selectDataTypeConfig(selection, config.ofType)
        : selectDataTypeConfig({}, config.ofType);
    return {
        ...config,
        args: selectedArgs,
        ofType: selectedOfType,
    };
};
const selectObject = (selection, config) => {
    const selectedFields = selectFieldsFromCollection(config.fields, selection.selections, (sel, field) => selectField(sel, field));
    return { ...config, fields: selectedFields };
};
const selectList = (selection, config) => {
    if (config.ofType.kind === 'SCALAR') {
        if (selection.selections && selection.selections.length > 0) {
            throw new Error(`Cannot select fields from scalar type '${config.ofType.typeName}'`);
        }
        return { ...config };
    }
    const ofTypeSelection = selection.selections?.length
        ? { selections: selection.selections }
        : {};
    const ofTypeConfig = selectDataTypeConfig(ofTypeSelection, config.ofType);
    const items = config.items?.map(item => selectDataTypeConfig(ofTypeSelection, item));
    return {
        ...config,
        ofType: ofTypeConfig,
        ...(items && { items }),
    };
};
const selectInputValue = (selection, config) => {
    const selectedOfType = selection.selections
        ? selectDataTypeConfig(selection, config.ofType)
        : selectDataTypeConfig({}, config.ofType);
    return {
        ...config,
        ofType: selectedOfType,
    };
};
const selectInputObject = (selection, config) => {
    const selectedInputFields = selectFieldsFromCollection(config.fields || [], selection.selections, (sel, inputField) => selectInputValue(sel, inputField));
    return { ...config, fields: selectedInputFields };
};
const selectEnum = (selection, config) => {
    return { ...config };
};
const selectDataTypeConfig = (sel, config) => {
    switch (config.kind) {
        case 'SCALAR':
            return selectScalar(sel, config);
        case 'FIELD':
            return selectField(sel, config);
        case 'OBJECT':
            return selectObject(sel, config);
        case 'LIST':
            return selectList(sel, config);
        case 'INPUT_OBJECT':
            return selectInputObject(sel, config);
        case 'INPUT_VALUE':
            return selectInputValue(sel, config);
        case 'ENUM':
            return selectEnum(sel, config);
        default:
            throw new Error(`Selection not supported for type kind '${config.kind}'`);
    }
};
const processArrayWithSelections = (dataTypeConfig, selection) => {
    if (selection.key) {
        return dataTypeConfig
            .filter(config => ('key' in config && config.key === selection.key) ||
            selection.key === '*')
            .map(config => {
            const childSelection = {
                ...selection,
                key: undefined,
            };
            return selectDataTypeConfig(childSelection, config);
        });
    }
    if (selection.selections?.length) {
        const selectedConfigs = [];
        const wildcardSelection = getWildcardSelection(selection.selections);
        const explicitKeys = getExplicitlySelectedKeys(selection.selections);
        for (const subSelection of selection.selections) {
            if (subSelection.key && subSelection.key !== '*') {
                const matchingConfigs = dataTypeConfig
                    .filter(config => 'key' in config && config.key === subSelection.key)
                    .map(config => selectDataTypeConfig(subSelection, config));
                selectedConfigs.push(...matchingConfigs);
            }
        }
        if (wildcardSelection) {
            const wildcardConfigs = dataTypeConfig
                .filter(config => 'key' in config && config.key && !explicitKeys.has(config.key))
                .map(config => selectDataTypeConfig(wildcardSelection, config));
            selectedConfigs.push(...wildcardConfigs);
        }
        return selectedConfigs;
    }
    return dataTypeConfig.map(config => selectDataTypeConfig(selection, config));
};
const getSelection = (selection) => (dataTypeConfig) => {
    if (Array.isArray(dataTypeConfig)) {
        return processArrayWithSelections(dataTypeConfig, selection);
    }
    return selectDataTypeConfig(selection, dataTypeConfig);
};
const convertGraphQLSelectionToSelection = (selectionNode) => {
    if (selectionNode.kind !== 'Field') {
        throw new Error(`Selection node kind '${selectionNode.kind}' not supported`);
    }
    const key = selectionNode.name.value;
    const selections = selectionNode.selectionSet?.selections?.map(convertGraphQLSelectionToSelection);
    return {
        ...(key && { key }),
        ...(selections && { selections }),
    };
};
const convertGraphQLQueryToSelection = (query) => {
    const ast = typeof query === 'string' ? parse(query) : query;
    const queryOperation = ast.definitions[0];
    if (queryOperation.kind !== 'OperationDefinition') {
        throw new Error('Query is not a valid operation definition');
    }
    if (queryOperation.operation !== 'query') {
        throw new Error('Query is not a valid query operation');
    }
    return {
        selections: queryOperation.selectionSet.selections.map(convertGraphQLSelectionToSelection),
    };
};
const isSelectionPointerMatch = (pointer) => (selection) => {
    const [pointerType, value] = pointer;
    if (pointerType !== 'key') {
        return false;
    }
    if (value === '*') {
        return true;
    }
    return selection.key === value;
};
const findSelection = (selectionLocator) => (selection) => {
    if (selectionLocator.length === 0) {
        return selection;
    }
    if (!selection.selections) {
        return null;
    }
    const [pointer, ...rest] = selectionLocator;
    const matches = selection.selections.filter(isSelectionPointerMatch(pointer));
    for (const match of matches) {
        const found = findSelection(rest)(match);
        if (found) {
            return found;
        }
    }
    for (const child of selection.selections) {
        const foundInChild = findSelection(selectionLocator)(child);
        if (foundInChild) {
            return foundInChild;
        }
    }
    return null;
};
const mergeSelections = (selections) => {
    const selectionMap = new Map();
    selections.forEach(selection => {
        const existing = selectionMap.get(selection.key);
        if (existing && existing.selections && selection.selections) {
            selectionMap.set(selection.key, {
                ...existing,
                selections: mergeSelections([
                    ...existing.selections,
                    ...selection.selections,
                ]),
            });
        }
        else {
            selectionMap.set(selection.key, selection);
        }
    });
    return Array.from(selectionMap.values());
};
const createSelectionFromValue = (value) => {
    if (Array.isArray(value)) {
        const itemSelections = value.flatMap(item => createSelectionFromValue(item).selections || []);
        return { selections: mergeSelections(itemSelections) };
    }
    if (typeof value === 'object' && value !== null) {
        const selections = Object.keys(value).map(key => {
            const nestedSelectionDefinition = createSelectionFromValue(value[key]);
            const selection = { key };
            if (nestedSelectionDefinition.selections &&
                nestedSelectionDefinition.selections.length > 0) {
                selection.selections = nestedSelectionDefinition.selections;
            }
            return selection;
        });
        return { selections };
    }
    return {};
};
const createSelectionFromLocators = (locators) => {
    const buildNestedSelection = (locator) => {
        const keyMatchers = locator.filter(([matchType]) => matchType === 'key');
        if (keyMatchers.length === 0)
            return null;
        return keyMatchers.reduceRight((inner, [, value]) => ({
            key: value,
            ...(inner && { selections: [inner] }),
        }), null);
    };
    const individualSelections = locators
        .map(buildNestedSelection)
        .filter((selection) => selection !== null);
    return { selections: mergeSelections(individualSelections) };
};
export const DataTypeSelection = {
    getSelection,
    convertGraphQLQueryToSelection,
    findSelection,
    createSelectionFromValue,
    createSelectionFromLocators,
};
//# sourceMappingURL=DataTypeSelection.js.map